import { useEffect, useRef, useState } from "react";

const GOLD = "#84DFF4";
const MUTED = "rgba(245, 245, 247,0.45)";
const LABEL = { fontFamily: '"Geist Variable", "Helvetica Neue", Arial, sans-serif', letterSpacing: "0.22em", fill: MUTED };

// ViewBox 800×300. Glass at x=400. Projector A low-left, B high-left; cones sweep right.
const RAYS = [
  { id: "a", from: [60, 215], hit: [400, 150], cone: [[400, 150], [800, 8], [800, 136]], label: "Projection A", lx: 28, ly: 236 },
  { id: "b", from: [60, 85], hit: [400, 150], cone: [[400, 150], [800, 164], [800, 292]], label: "Projection B", lx: 28, ly: 72 },
];

const VIEW_X = 700;
const Y_MIN = 58;
const Y_MAX = 240;
const Y_MID = (Y_MIN + Y_MAX) / 2;
const Y_AMP = (Y_MAX - Y_MIN) / 2;
const PERIOD = 6.4;
const IDLE_RESUME = 2500;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
// Zone A ends ~y140 on the viewing line, zone B begins ~y160; the gap is where neither image is seen.
const smooth = (y) => { const t = clamp((y - 135) / 30, 0, 1); return t * t * (3 - 2 * t); };

// What the viewer sees: the same small prism, from the left (A) or the right (B). Local coords, frame-centred.
const FRAME = { w: 40, h: 44, x: VIEW_X + 16 };
const PRISM = {
  front: "-6,-4 6,-4 6,8 -6,8",
  a: { side: "-6,-4 -11,-8 -11,4 -6,8", top: "-6,-4 -11,-8 1,-8 6,-4", glint: [-8.5, -2] },
  b: { side: "6,-4 11,-8 11,4 6,8", top: "-6,-4 -1,-8 11,-8 6,-4", glint: [8.5, -2] },
};

const Frame = ({ id, y, shown }) => (
  <g data-testid={`lf-frame-${id}`} opacity={shown} transform={`translate(${FRAME.x + FRAME.w / 2} ${y})`} style={{ transition: "opacity 0.18s linear" }}>
    <polygon points={PRISM[id].top} fill={GOLD} fillOpacity="0.22" stroke={GOLD} strokeOpacity="0.8" strokeWidth="0.6" strokeLinejoin="round" />
    <polygon points={PRISM[id].side} fill={GOLD} fillOpacity="0.1" stroke={GOLD} strokeOpacity="0.8" strokeWidth="0.6" strokeLinejoin="round" />
    <polygon points={PRISM.front} fill={GOLD} fillOpacity="0.32" stroke={GOLD} strokeOpacity="0.9" strokeWidth="0.6" strokeLinejoin="round" />
    <circle cx={PRISM[id].glint[0]} cy={PRISM[id].glint[1]} r="0.9" fill="#fff" fillOpacity="0.9" />
    <text x="0" y="17.5" textAnchor="middle" className="lf-frame-label" style={{ ...LABEL, fill: "rgba(245, 245, 247,0.6)" }}>{id.toUpperCase()}</text>
  </g>
);

const useViewer = (reduce) => {
  const [y, setY] = useState(Y_MIN + 14);
  const [dragging, setDragging] = useState(false);
  const idleAt = useRef(0);
  const phase = useRef(Math.asin((Y_MIN + 14 - Y_MID) / Y_AMP));
  const yRef = useRef(y);
  yRef.current = y;

  useEffect(() => {
    if (reduce) return undefined;
    let frame;
    let last = performance.now();
    let resumed = false;
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!dragging && now - idleAt.current > IDLE_RESUME) {
        if (!resumed) { phase.current = Math.asin(clamp((yRef.current - Y_MID) / Y_AMP, -1, 1)); resumed = true; }
        phase.current += (dt * 2 * Math.PI) / PERIOD;
        setY(Y_MID + Y_AMP * Math.sin(phase.current));
      } else {
        resumed = false;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reduce, dragging]);

  const place = (svg, clientY) => {
    const pt = new DOMPoint(0, clientY).matrixTransform(svg.getScreenCTM().inverse());
    setY(clamp(pt.y, Y_MIN, Y_MAX));
    idleAt.current = performance.now();
  };
  return { y, dragging, setDragging, place };
};

export const LightfieldSchematic = () => {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const fine = window.matchMedia("(pointer: fine)").matches;
  const { y, dragging, setDragging, place } = useViewer(reduce);
  const svgRef = useRef(null);
  const t = smooth(y);
  const coneA = 1 - 0.7 * t;
  const coneB = 0.3 + 0.7 * t;

  const down = (e) => { if (!fine) return; e.currentTarget.setPointerCapture(e.pointerId); setDragging(true); place(svgRef.current, e.clientY); };
  const move = (e) => { if (dragging) place(svgRef.current, e.clientY); };
  const up = () => setDragging(false);

  return (
  <svg ref={svgRef} data-testid="lightfield-schematic" viewBox="0 0 800 300" role="img" aria-label="Optical schematic: two projectors strike a coated glass sheet and emerge as two distinct viewing cones. Drag the viewer to change perspective."
    className="h-auto w-full select-none sm:h-64 lg:h-80" preserveAspectRatio="xMidYMid meet">
    <defs>
      <filter id="lf-glow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="2.2" /></filter>
      <filter id="lf-soft" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="6" /></filter>
      <linearGradient id="lf-cone" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor={GOLD} stopOpacity="0.55" /><stop offset="0.5" stopColor={GOLD} stopOpacity="0.16" /><stop offset="1" stopColor={GOLD} stopOpacity="0" />
      </linearGradient>
      <linearGradient id="lf-glass" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#fff" stopOpacity="0" /><stop offset="0.15" stopColor="#fff" stopOpacity="0.5" /><stop offset="0.85" stopColor="#fff" stopOpacity="0.5" /><stop offset="1" stopColor="#fff" stopOpacity="0" />
      </linearGradient>
    </defs>

    {RAYS.map((r) => (
      <g key={r.id} data-testid={`lf-cone-${r.id}`} opacity={r.id === "a" ? coneA : coneB} style={{ transition: dragging ? "none" : "opacity 0.3s linear" }}>
        <polygon points={r.cone.map((p) => p.join(",")).join(" ")} fill="url(#lf-cone)" filter="url(#lf-soft)" className={`lf-cone lf-cone-${r.id}`} />
        <polygon points={r.cone.map((p) => p.join(",")).join(" ")} fill="url(#lf-cone)" opacity="0.6" />
      </g>
    ))}

    <line x1="400" x2="400" y1="30" y2="270" stroke="#fff" strokeOpacity="0.5" strokeWidth="4" filter="url(#lf-glow)" />
    <line x1="400" x2="400" y1="30" y2="270" stroke="url(#lf-glass)" strokeWidth="1.2" />

    {RAYS.map((r) => (
      <g key={r.id}>
        <line x1={r.from[0]} y1={r.from[1]} x2={r.hit[0]} y2={r.hit[1]} stroke={GOLD} strokeOpacity="0.28" strokeWidth="1" />
        <line x1={r.from[0]} y1={r.from[1]} x2={r.hit[0]} y2={r.hit[1]} stroke={GOLD} strokeWidth="1.2" strokeLinecap="round" strokeDasharray="14 332" className={`lf-ray lf-ray-${r.id}`} pathLength="346" />
        <circle cx={r.from[0]} cy={r.from[1]} r="2.2" fill={GOLD} />
        <circle cx={r.from[0]} cy={r.from[1]} r="6" fill="none" stroke={GOLD} strokeOpacity="0.35" strokeWidth="0.6" />
        <text x={r.lx} y={r.ly} className="lf-label" style={LABEL}>{r.label.toUpperCase()}</text>
      </g>
    ))}

    <circle cx="400" cy="150" r="3" fill="#fff" fillOpacity="0.9" className="lf-impact" filter="url(#lf-glow)" />
    <circle cx="400" cy="150" r="1.6" fill="#fff" />

    {/* Leader: controllable scattering matrix */}
    <line x1="400" y1="30" x2="400" y2="18" stroke={MUTED} strokeWidth="0.5" />
    <line x1="400" y1="18" x2="470" y2="18" stroke={MUTED} strokeWidth="0.5" />
    <text x="476" y="20.5" className="lf-label" style={LABEL}>CONTROLLABLE SCATTERING MATRIX</text>

    {/* Leader: viewing zones */}
    <line x1={VIEW_X} y1="40" x2={VIEW_X} y2="286" stroke={MUTED} strokeWidth="0.5" strokeDasharray="2 4" />

    {/* Viewer on the viewing line: sight line to the impact point, eye marker, label. Draggable on desktop. */}
    <line x1={VIEW_X} y1={y} x2="400" y2="150" stroke="#fff" strokeOpacity="0.18" strokeWidth="0.6" strokeDasharray="1.5 3" />
    <g data-testid="lf-viewer" data-cursor data-dragging={dragging} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      style={{ cursor: fine ? (dragging ? "grabbing" : "grab") : undefined, touchAction: fine ? "none" : undefined }}>
      <rect x={VIEW_X - 18} y={Y_MIN - 12} width="36" height={Y_MAX - Y_MIN + 24} fill="transparent" />
      <circle cx={VIEW_X} cy={y} r={dragging ? 11 : 8} fill="none" stroke="#fff" strokeOpacity={dragging ? 0.55 : 0.3} strokeWidth="0.6" style={{ transition: "r 0.3s, stroke-opacity 0.3s" }} />
      <circle cx={VIEW_X} cy={y} r="2.6" fill="#fff" fillOpacity="0.95" filter="url(#lf-glow)" />
      {/* Live frame: what this eye receives, flipping as the viewer crosses zones */}
      <line x1={VIEW_X + 3} y1={y} x2={FRAME.x} y2={y} stroke={MUTED} strokeWidth="0.5" />
      <rect x={FRAME.x} y={y - FRAME.h / 2} width={FRAME.w} height={FRAME.h} fill="#000" fillOpacity="0.55" stroke="#fff" strokeOpacity="0.28" strokeWidth="0.6" />
      <Frame id="a" y={y} shown={1 - t} />
      <Frame id="b" y={y} shown={t} />
      <text x={FRAME.x} y={y + FRAME.h / 2 + 9} className="lf-label" style={LABEL}>VIEWER</text>
      {fine && <text x={FRAME.x + 40} y={y + FRAME.h / 2 + 9} className="lf-label lf-hint" style={{ ...LABEL, fill: "rgba(132,223,244,0.6)" }} opacity={dragging ? 0 : 1}>· DRAG</text>}
    </g>
    <text x="640" y="296" className="lf-label" style={LABEL}>VIEWING ZONES</text>
    <text x="656" y="80" className="lf-label" style={LABEL}>A</text>
    <text x="656" y="232" className="lf-label" style={LABEL}>B</text>

    <style>{`
      .lf-label { font-size: 7.5px; }
      .lf-hint { font-size: 6px; transition: opacity 0.3s; }
      .lf-frame-label { font-size: 5.5px; }
      @media (max-width: 640px) { .lf-label { font-size: 13px; letter-spacing: 0.16em; } .lf-frame-label { font-size: 7px; } }
      .lf-ray { animation: lf-travel 3.2s linear infinite; }
      .lf-ray-b { animation-delay: -1.6s; }
      .lf-cone { animation: lf-bloom 3.2s ease-in-out infinite; transform-origin: 400px 150px; transform-box: view-box; }
      .lf-cone-b { animation-delay: -1.6s; }
      .lf-impact { animation: lf-pulse 1.6s ease-in-out infinite; }
      @keyframes lf-travel { from { stroke-dashoffset: 346; } to { stroke-dashoffset: 0; } }
      @keyframes lf-bloom { 0%, 70% { opacity: 0.55; transform: scaleX(0.985); } 85% { opacity: 1; transform: scaleX(1); } 100% { opacity: 0.55; transform: scaleX(0.985); } }
      @keyframes lf-pulse { 0%, 100% { opacity: 0.5; r: 3; } 50% { opacity: 1; r: 5; } }
      @media (prefers-reduced-motion: reduce) { .lf-ray, .lf-cone, .lf-impact { animation: none; } .lf-ray { stroke-dasharray: none; } }
    `}</style>
  </svg>
  );
};
